import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { urlParameterCodes } from '@/constants/urlParams';
import { getUrlParamValue } from '@/ui/utils/client-url-helpers';
import {
  CART_DEFAULT_TIMEOUT_IN_MINUTES,
  CART_LOCAL_STORAGE_ID,
  MILISECONDS_IN_MINUTE,
} from '@/utils/constants';
import { generateRandomId } from '@/ui/utils/helpers';
import { AreaVars } from '@/integrations/crystallize/types/localization';
import { ICartState } from './types';
import { CartService } from '../../services/CartService';

const cartTimeout =
  parseInt(process.env.NEXT_PUBLIC_CART_TIMEOUT_IN_MINUTES || '', 10) ||
  CART_DEFAULT_TIMEOUT_IN_MINUTES;

const useCartStore = create<ICartState>()(
  persist(
    (set, get) => ({
      items: { products: [], bundles: [] },
      isCartDisplayed: getUrlParamValue(urlParameterCodes.cart) === 'true',
      showCartUpselling: false,
      lastUpdated: null,
      voucherCodes: [],
      referralVoucherCode: null,
      lastAddedToCart: null,
      showAdditionalInformation: false,
      activeAdditionalInfoBoxId: undefined,
      country: null,
      areaVariables: null,
      showNotifyMe: { show: false, productVariant: null, bundle: null },
      addProduct: (product, productVariant, options) =>
        set((state) => CartService.addProductToCart(state, product, productVariant, options)),
      addBundle: (bundle, options) => {
        const finalBundle = options?.quantity
          ? { ...bundle, quantity: options.quantity }
          : bundle;
        set((state) => CartService.addBundleToCart(state, finalBundle));
      },
      addCartItem: (cartItem) => set((state) => CartService.addItemToCart(state, cartItem)),
      removeItem: (cartItemId: string) =>
        set((state) => CartService.removeProductFromCart(state, cartItemId)),
      removeBundle: (cartBundleId: string, upgradeProgramDeviceId?: string) =>
        set((state) =>
          CartService.removeBundleFromCart(state, cartBundleId, upgradeProgramDeviceId)),
      updateItemQuantity: (cartItemId, quantity) =>
        set((state) => CartService.updateItemQuantityInCart(state, cartItemId, quantity)),
      updateBundleQuantity: (cartBundleId: string, quantity: number) =>
        set((state) => CartService.updateBundleQuantityInCart(state, cartBundleId, quantity)),
      getTotal: () => CartService.calculateTotalCartValue(get()),
      getTotalDiscount: () => CartService.calculateTotalCartDiscount(get()),
      getTotalAfterDiscount: () => CartService.calculateTotalCartValueAfterDiscount(get()),
      numberOfItems: () => CartService.calculateTotalNumberOfItems(get()),
      isEmpty: () => CartService.isCartEmpty(get()),
      getPriceVariant: () => get().areaVariables?.priceVariant,
      toggleCartDisplay: () => set((state) => CartService.toggleCartDisplayState(state)),
      // Reset the drawer's transient display state. Called after the drawer finishes animating
      // closed so the cross-sell content (upselling, "added to cart" banner, extra layer) stays
      // stable during the leave transition instead of vanishing mid-animation.
      resetCartDisplayTransient: () =>
        set({
          showCartUpselling: false,
          showAdditionalInformation: false,
          lastAddedToCart: null,
          activeAdditionalInfoBoxId: undefined,
        }),
      clearCart: () => set({ items: { products: [], bundles: [] } }),
      setCartUpsellingData: (data) => set({ cartUpsellingData: data }),
      setShowCartUpselling: (show) => set({ showCartUpselling: show }),
      setShowAdditionalInformation: (show) => set({ showAdditionalInformation: show }),
      getShowAdditionalInformation: () => get().showAdditionalInformation,
      setAdditionalInformationTiles: (tiles) => set({ additionalInformationTiles: tiles }),
      setActiveAdditionalInfoBoxId: (id) => set({ activeAdditionalInfoBoxId: id }),
      addVoucherCode: (voucherCode) =>
        set((state) => ({ voucherCodes: [voucherCode, ...state.voucherCodes] })),
      removeVoucherCode: (voucherCodeToRemove) =>
        set((state) => ({
          voucherCodes: state.voucherCodes.filter((code) => code !== voucherCodeToRemove),
        })),
      setReferralVoucherCode: (voucherCode) => set({ referralVoucherCode: voucherCode }),
      clearReferralVoucherCode: () => set({ referralVoucherCode: null }),
      isThereAnItemLinkedWithID: (nukiId, sku) =>
        CartService.isThereAnItemLinkedWithID(get(), nukiId, sku),
      containsProductById: (productId) => CartService.containsProductById(get(), productId),
      timeoutCheck: () => {
        const { lastUpdated, clearCart } = get();
        if (lastUpdated) {
          const now = new Date();
          const lastUpdateDate = new Date(lastUpdated);
          const diffInMinutes = Math.floor(
            (now.getTime() - lastUpdateDate.getTime()) / MILISECONDS_IN_MINUTE,
          );
          if (diffInMinutes > cartTimeout) {
            clearCart();
          }
        }
      },
      setCountry: (country: string) => {
        const { country: currentCountry, clearCart } = get();
        if (currentCountry !== country) {
          clearCart();
          set({ country });
        }
      },
      setAreaVariables: (areaVariables: AreaVars) => {
        const { areaVariables: currentAreaVariables } = get();
        if (currentAreaVariables?.id !== areaVariables.id) {
          set({ areaVariables });
        }
      },
      getCostBreakdown: (shippingCost = 0) => CartService.getCostBreakdown(get(), shippingCost),
      getDeliveryTime: () => CartService.getDeliveryTime(get()),
      setShowNotifyMe: (show, productVariant, bundle) =>
        set({ showNotifyMe: { show, productVariant, bundle } }),
    }),
    {
      name: CART_LOCAL_STORAGE_ID,
      version: 2,
      migrate: (persistedState: any, version: number): ICartState => {
        const migrations: ((state: ICartState) => ICartState)[] = [
          (state) =>
            // Migration for version 1
            ({
              ...state,
              items: { products: [], bundles: [] },
            }),
          (state) =>
            // Migration for version 2: Add cartItemId to each product
            ({
              ...state,
              items: {
                ...state.items,
                products: state.items.products.map((product) => ({
                  ...product,
                  cartItemId: generateRandomId(),
                })),
              },
            }),
        ];

        let state: ICartState = persistedState;

        for (let i = version; i < migrations.length; i += 1) {
          state = migrations[i](state);
        }

        return state;
      },
      partialize(state) {
        const {
          isCartDisplayed,
          showCartUpselling,
          showAdditionalInformation,
          additionalInformationTiles,
          cartUpsellingData,
          activeAdditionalInfoBoxId,
          ...persistableState
        } = state;
        return persistableState;
      },
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.timeoutCheck();
        }
      },
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export default useCartStore;
